import React, { useState } from 'react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { useToolResultContent } from './ToolResultDataContext';
import { Spinner } from './codeBlockUtils';

import { useTranslation } from 'react-i18next';

function KeyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.75" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
    </svg>
  );
}

function AWSIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M6.763 10.036c0 .296.032.535.088.71.064.176.144.368.256.576.04.064.056.128.056.184 0 .08-.048.16-.152.24l-.504.336a.383.383 0 01-.208.072c-.08 0-.16-.04-.24-.112a2.47 2.47 0 01-.288-.376 6.18 6.18 0 01-.248-.471c-.622.734-1.405 1.101-2.347 1.101-.67 0-1.205-.191-1.596-.574-.392-.382-.59-.891-.59-1.526 0-.67.239-1.214.726-1.626.487-.413 1.133-.619 1.955-.619.272 0 .551.024.838.064.287.04.583.104.887.176v-.56c0-.583-.123-.99-.363-1.222-.248-.232-.655-.344-1.228-.344-.264 0-.535.032-.814.104-.28.072-.551.16-.814.272a2.178 2.178 0 01-.248.088.422.422 0 01-.12.024c-.104 0-.156-.072-.156-.224v-.351c0-.12.016-.208.056-.256.04-.048.112-.096.216-.144.264-.136.582-.248.95-.336.37-.096.76-.144 1.172-.144.892 0 1.545.203 1.963.607.41.405.619 1.02.619 1.847v2.434zm-3.243 1.214c.264 0 .535-.048.814-.144.28-.096.527-.272.735-.512.12-.144.208-.304.256-.487.048-.184.072-.408.072-.672v-.32a6.746 6.746 0 00-.727-.136 5.979 5.979 0 00-.742-.048c-.528 0-.914.104-1.172.32-.256.215-.384.519-.384.911 0 .368.096.647.296.83.191.176.463.264.822.264l.03-.006zm6.137.836c-.136 0-.224-.024-.28-.072-.056-.04-.104-.136-.144-.264l-1.612-5.306a1.178 1.178 0 01-.064-.272c0-.104.056-.16.168-.16h.687c.144 0 .24.024.295.072.056.04.096.136.128.264l1.149 4.526 1.068-4.526c.032-.136.072-.224.128-.264.056-.04.16-.072.295-.072h.56c.144 0 .24.024.296.072.056.04.104.136.128.264l1.083 4.582 1.188-4.582c.032-.136.08-.224.128-.264.056-.04.152-.072.296-.072h.655c.112 0 .168.056.168.16 0 .032-.008.064-.016.104-.008.04-.024.096-.048.176l-1.652 5.306c-.04.128-.08.224-.144.264-.056.048-.152.072-.28.072h-.6c-.144 0-.24-.024-.296-.072-.056-.04-.104-.136-.136-.272l-1.06-4.43-1.052 4.422c-.04.136-.08.224-.136.272-.056.048-.16.072-.296.072h-.6zm8.878.152c-.408 0-.815-.048-1.21-.144-.392-.096-.695-.2-.903-.32-.127-.072-.208-.152-.232-.224a.56.56 0 01-.04-.208v-.368c0-.152.056-.224.168-.224.04 0 .08.008.12.024.04.016.104.048.168.08.232.104.487.184.766.24.287.056.567.08.854.08.455 0 .808-.08 1.052-.24a.786.786 0 00.368-.688.71.71 0 00-.2-.511c-.136-.136-.392-.256-.767-.368l-1.1-.344c-.56-.176-.975-.432-1.234-.768a1.878 1.878 0 01-.384-1.147c0-.328.072-.62.216-.87.144-.248.336-.464.576-.632.24-.176.51-.304.814-.392.304-.088.622-.128.95-.128.168 0 .344.008.511.032.176.024.336.056.496.088.152.04.296.08.432.128.136.048.24.096.32.144.112.072.192.144.232.216.04.072.064.16.064.272v.336c0 .152-.056.232-.16.232-.056 0-.144-.024-.264-.08-.399-.176-.847-.264-1.347-.264-.408 0-.727.064-.95.2-.224.136-.336.344-.336.624 0 .2.072.368.224.504.152.136.432.272.83.392l1.083.344c.55.176.95.424 1.194.743.247.32.368.687.368 1.1 0 .336-.072.64-.208.903-.144.264-.336.495-.583.68-.248.192-.543.336-.87.432-.344.104-.703.152-1.084.152z"/>
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg className={`h-3 w-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

interface AWSRole {
  integrationId: string;
  name: string;
  awsAccountId: string;
  roleArn: string;
  externalId: string;
  status: string;
}

function RoleRow({ role }: { role: AWSRole }) {
  const isActive = role.status?.toLowerCase() === 'active' || role.status?.toLowerCase() === 'connected';
  return (
    <div className="flex items-start gap-3 border-t border-gray-800/60 px-3 py-2.5">
      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-[#FF9900]/10">
        <AWSIcon className="h-3.5 w-3.5 text-[#FF9900]" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-200">{role.name}</span>
          <span className={`rounded px-1.5 py-px text-xs font-semibold uppercase tracking-wider ${
            isActive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-700 text-gray-500'
          }`}>
            {role.status ?? 'unknown'}
          </span>
        </div>
        <span className="font-mono text-xs text-gray-600">{role.awsAccountId}</span>
        <span className="truncate font-mono text-xs text-gray-700" title={role.roleArn}>
          {role.roleArn}
        </span>
      </div>
    </div>
  );
}

/**
 * Renders shellAssumeRole and shellListAssumableRoles tool calls.
 */
export const ShellCredentialBlock: React.FC<{ content: ToolUseBlock }> = ({ content }) => {
  const { t } = useTranslation('pleri/chat');
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const isPending = useChatStore((s) =>
    activeConversationId
      ? (s.conversations[activeConversationId]?.pendingToolUses ?? new Set()).has(content.id)
      : false,
  );
  const resultJson = useToolResultContent(content.id);
  const isRunning = isPending || !resultJson;

  const isAssume = content.name === 'shellAssumeRole';
  const [open, setOpen] = useState(false);

  // Parse input
  const roleArn = typeof content.input?.roleArn === 'string' ? content.input.roleArn : null;
  const session = typeof content.input?.session === 'string' ? content.input.session : 'main';

  // Parse result
  let roles: AWSRole[] = [];
  let assumeMessage = '';
  let assumeRoleArn = '';
  let expiresAt = '';
  let isError = false;

  if (resultJson && !isRunning) {
    try {
      const parsed = JSON.parse(resultJson);
      if (isAssume) {
        assumeMessage = typeof parsed === 'string' ? parsed : parsed?.message ?? '';
        assumeRoleArn = parsed?.assumedRoleArn ?? roleArn ?? '';
        expiresAt = parsed?.expiresAt ?? '';
        isError = assumeMessage.toLowerCase().includes('fail') || assumeMessage.toLowerCase().includes('error');
      } else {
        roles = parsed?.roles ?? [];
      }
    } catch {
      if (isAssume) {
        assumeMessage = resultJson.replace(/^"|"$/g, '');
      }
    }
  }

  // Short account ID from ARN
  const accountId = assumeRoleArn.match(/::(\d+):/)?.[1];
  const roleName = assumeRoleArn.split('/').pop();

  return (
    <div className="overflow-hidden rounded-xl border border-gray-800 bg-gray-950 shadow-sm">
      {/* Header */}
      <button
        className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-900/40 transition-colors"
        onClick={() => !isAssume && setOpen((v) => !v)}
        style={{ cursor: isAssume ? 'default' : 'pointer' }}
      >
        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-[#FF9900]/10">
          <KeyIcon className="h-3.5 w-3.5 text-[#FF9900]" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          {isAssume ? (
            <>
              <span className="text-xs font-medium text-gray-300">
                {isRunning
                  ? t('assumingRole')
                  : isError
                    ? t('roleError')
                    : t('roleAssumed')}
              </span>
              {!isRunning && !isError && roleName && (
                <span className="font-mono text-xs text-gray-600">
                  {t('roleAccountSession', {
                    roleName,
                    accountId: accountId ?? '',
                    session,
                  })}
                </span>
              )}
            </>
          ) : (
            <span className="text-xs font-medium text-gray-300">
              {isRunning
                ? t('fetchingAwsAccounts')
                : t('accountsAvailable', { count: roles.length })}
            </span>
          )}
        </div>

        {isRunning ? (
          <Spinner className="text-yellow-400" />
        ) : isAssume && !isError ? (
          <span className="flex-shrink-0 rounded-md bg-emerald-900/30 px-1.5 py-0.5 text-xs font-medium text-emerald-400">
            {t('active')}
          </span>
        ) : !isAssume && roles.length > 0 ? (
          <span className="ml-1 flex-shrink-0 text-gray-600">
            <ChevronIcon open={open} />
          </span>
        ) : isError ? (
          <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500" />
        ) : (
          <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-500" />
        )}
      </button>
      {/* Assume role: expiry */}
      {isAssume && !isRunning && expiresAt && !isError && (
        <div className="border-t border-gray-800 px-3 py-1.5">
          <span className="text-xs text-gray-600">
            {t('expiresAt', { time: new Date(expiresAt).toLocaleTimeString() })}
          </span>
        </div>
      )}
      {/* Assume role: error */}
      {isAssume && !isRunning && isError && (
        <div className="border-t border-gray-800 px-3 py-2">
          <span className="text-xs text-red-400">{assumeMessage}</span>
        </div>
      )}
      {/* List roles: expanded */}
      {!isAssume && open && roles.length > 0 && (
        <div>
          {roles.map((r) => <RoleRow key={r.integrationId} role={r} />)}
        </div>
      )}
    </div>
  );
};
